import React, { useState, useEffect } from 'react';
import { X, Save, AlertCircle, Calendar } from 'lucide-react';
import { supabase } from '../../../lib/supabase';
import assessmentService from '../../../services/assessmentService';
import { useAuth } from '../../../contexts/AuthContext';

const isAcademicSemesterOpen = (semester) => {
  if (!semester || (semester.is_active !== true && String(semester.calendar_status || '').toLowerCase() !== 'active')) return false;
  if (!semester.end_date) return true;
  const end = new Date(`${semester.end_date}T23:59:59`);
  return !Number.isNaN(end.getTime()) && new Date() <= end;
};

const findCalendarPeriod = (academicSemester, calendarSemesters = []) => {
  if (!academicSemester) return null;
  const exactDates = calendarSemesters.find(period =>
    String(period.start_date || '') === String(academicSemester.start_date || '') &&
    String(period.end_date || '') === String(academicSemester.end_date || '')
  );
  if (exactDates) return exactDates;
  const overlapping = calendarSemesters
    .filter(period => {
      const calendarStart = new Date(period.start_date);
      const calendarEnd = new Date(period.end_date);
      const academicStart = new Date(academicSemester.start_date);
      const academicEnd = new Date(academicSemester.end_date);
      return [calendarStart, calendarEnd, academicStart, academicEnd].every(date => !Number.isNaN(date.getTime()))
        && calendarStart <= academicEnd && calendarEnd >= academicStart;
    })
    .sort((left, right) => {
      const academicStart = new Date(academicSemester.start_date).getTime();
      const academicEnd = new Date(academicSemester.end_date).getTime();
      const overlap = period => Math.max(0, Math.min(new Date(period.end_date).getTime(), academicEnd) - Math.max(new Date(period.start_date).getTime(), academicStart));
      return overlap(right) - overlap(left);
    });
  if (overlapping[0]) return overlapping[0];
  return calendarSemesters.find(period => {
    if (Number(period.term_number) !== Number(academicSemester.semester_number)) return false;
    const calendarStart = new Date(period.start_date);
    const calendarEnd = new Date(period.end_date);
    const academicStart = new Date(academicSemester.start_date);
    const academicEnd = new Date(academicSemester.end_date);
    return [calendarStart, calendarEnd, academicStart, academicEnd].every(date => !Number.isNaN(date.getTime()))
      && calendarStart <= academicEnd && calendarEnd >= academicStart;
  }) || null;
};

const calendarPeriodLabel = (period) => {
  if (!period) return '';
  const name = period.name || period.calendar_name || '';
  const yearName = period.academic_year_name || period.academic_year?.name || '';
  return [name, yearName].filter(Boolean).join(', ');
};

const AssessmentTimelineModal = ({ isOpen, onClose, timeline, onSuccess, defaultSemester = null, defaultCalendarSemester = null, calendarSemesters = [] }) => {
  const { userProfile, user } = useAuth();
  const [formData, setFormData] = useState({
    unit_id: '',
    semester_id: '',
    calendar_semester_id: '',
    title: '',
    total_marks: 30,
    start_date: '',
    end_date: '',
    submission_deadline: '',
    assessment_status: 'active',
    allow_trainer_override: false,
    original_total_marks: null,
    reopened_for_remediation: false
  });

  const [autoAssNumber, setAutoAssNumber] = useState(1);
  const [availableUnits, setAvailableUnits] = useState([]);
  const [activeSemester, setActiveSemester] = useState(null);
  const [semesterPeriods, setSemesterPeriods] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (isOpen) {
      loadData();
    }
  }, [isOpen]);

  useEffect(() => {
    if (timeline) {
      let deadlineValue = '';
      if (timeline?.submission_deadline) {
        try {
          deadlineValue = new Date(timeline.submission_deadline).toISOString().slice(0, 16);
        } catch (e) {
          deadlineValue = timeline.submission_deadline;
        }
      }

      setFormData({
        unit_id: timeline?.unit_id || '',
        semester_id: timeline?.semester_id || '',
        calendar_semester_id: timeline?.calendar_semester_id || timeline?.calendar_period?.id || '',
        title: timeline?.title || timeline?.originalTitle || '',
        total_marks: timeline?.total_marks || 30,
        start_date: timeline?.start_date ? timeline?.start_date?.split('T')?.[0] : '',
        end_date: timeline?.end_date ? timeline?.end_date?.split('T')?.[0] : '',
        submission_deadline: deadlineValue,
        assessment_status: timeline?.assessment_status || 'active',
        allow_trainer_override: timeline?.allow_trainer_override || false,
        original_total_marks: timeline?.original_total_marks || null,
        reopened_for_remediation: timeline?.reopened_for_remediation || false
      });
      setAutoAssNumber(timeline?.assessment_number || 1);
    } else {
      const today = new Date().toISOString().split('T')[0];
      const twoWeeksLaterDate = new Date(Date.now() + 14 * 24 * 60 * 60 * 1000);
      const twoWeeksLater = twoWeeksLaterDate.toISOString().split('T')[0];
      const defaultDeadline = `${twoWeeksLater}T23:59`;
      
      setFormData(prev => ({
        ...prev,
        unit_id: '',
        title: `CAM ${autoAssNumber}`,
        total_marks: 30,
        start_date: today,
        end_date: twoWeeksLater,
        submission_deadline: defaultDeadline,
        assessment_status: 'active',
        allow_trainer_override: false,
        original_total_marks: null
      }));
    }
  }, [timeline, autoAssNumber]);

  const loadData = async () => {
    try {
      // 1. Fetch available units
      const { data: unitsData } = await supabase
        .from('units')
        .select('id, code, name')
        .eq('is_active', true);
      setAvailableUnits(unitsData || []);

      // 2. Fetch active semester
      const { data: semestersData } = await supabase
        .from('academic_semesters')
        .select('id, semester_number, semester_type, start_date, end_date, is_active, program_class_id')
        .order('start_date', { ascending: false });

      const enrichedPeriods = (semestersData || []).map(period => {
        const calendarPeriod = findCalendarPeriod(period, calendarSemesters);
        return {
          ...period,
          calendar_name: calendarPeriod?.name || null,
          calendar_year_name: calendarPeriod?.academic_year_name || null,
          calendar_status: calendarPeriod?.status || null,
          calendar_semester_id: calendarPeriod?.id || null
        };
      });
      setSemesterPeriods(enrichedPeriods);
      const currentActive = enrichedPeriods.find(semester => isAcademicSemesterOpen(semester))
        || defaultSemester
        || null;
      const currentCalendar = calendarSemesters.find(period => isAcademicSemesterOpen({ ...period, calendar_status: period.status }))
        || defaultCalendarSemester
        || null;
      
      if (currentActive || currentCalendar) {
        setFormData(prev => ({
          ...prev,
          semester_id: timeline?.semester_id || (!timeline && !currentCalendar ? currentActive?.id || '' : (timeline ? prev.semester_id : '')),
          calendar_semester_id: timeline?.calendar_semester_id || (!timeline ? currentCalendar?.id || '' : prev.calendar_semester_id)
        }));
        setActiveSemester(currentActive || null);
      }

      // 3. Auto-generate the next assessment number within the selected
      // semester. CAM numbering starts again when a new semester starts.
      const targetSemesterId = timeline?.semester_id || currentActive?.id;
      let timelineQuery = supabase
        .from('assessment_timelines')
        .select('assessment_number')
        .eq('assessment_type', 'formative')
        .order('assessment_number', { ascending: false })
        .limit(1);
      if (targetSemesterId) timelineQuery = timelineQuery.eq('semester_id', targetSemesterId);
      if (timeline?.calendar_semester_id || (!timeline && currentCalendar?.id)) {
        timelineQuery = supabase
          .from('assessment_timelines')
          .select('assessment_number')
          .eq('assessment_type', 'formative')
          .eq('calendar_semester_id', timeline?.calendar_semester_id || currentCalendar.id)
          .order('assessment_number', { ascending: false })
          .limit(1);
      }
      const { data: existingTimelines } = await timelineQuery;

      const nextNum = (existingTimelines?.[0]?.assessment_number || 0) + 1;
      setAutoAssNumber(nextNum);
      if (!timeline) {
        setFormData(prev => ({ ...prev, title: `CAM ${nextNum}` }));
      }
    } catch (err) {
      console.warn('Error loading timeline modal data:', err);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e?.target;
    setFormData(prev => {
      const updated = {
        ...prev,
        [name]: type === 'checkbox' ? checked 
          : type === 'number' ? parseFloat(value) || 0 
          : value
      };
      if (name === 'end_date' && (!prev.submission_deadline || prev.submission_deadline.startsWith(prev.end_date))) {
        updated.submission_deadline = `${value}T23:59`;
      }
      return updated;
    });
  };

  const selectedCalendarPeriod = calendarSemesters.find(period => String(period.id) === String(formData.calendar_semester_id))
    || (defaultCalendarSemester && String(defaultCalendarSemester.id) === String(formData.calendar_semester_id) ? defaultCalendarSemester : null);
  const selectedPeriod = semesterPeriods.find(period => String(period.id) === String(formData.semester_id))
    || (String(activeSemester?.id) === String(formData.semester_id) ? activeSemester : null)
    || (timeline?.semester && String(timeline.semester.id) === String(formData.semester_id) ? timeline.semester : null)
    || (defaultSemester && String(defaultSemester.id) === String(formData.semester_id) ? defaultSemester : null);
  const selectedPeriodIsActive = selectedCalendarPeriod
    ? isAcademicSemesterOpen({ ...selectedCalendarPeriod, calendar_status: selectedCalendarPeriod.status })
    : isAcademicSemesterOpen(selectedPeriod);

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setLoading(true);
    setError(null);

    try {
      if (!formData?.title?.trim()) {
        throw new Error('Please enter an assessment title.');
      }
      if (!formData?.start_date || !formData?.end_date) {
        throw new Error('Please specify start and closing dates.');
      }
      if (!formData?.submission_deadline) {
        throw new Error('Please specify the submission date (used for POE upload deadline).');
      }

      // Retrieve current authenticated user ID to satisfy RLS created_by = auth.uid()
      const { data: authData } = await supabase.auth.getUser();
      const currentUserId = authData?.user?.id || userProfile?.id || user?.id;

      // Auto-extract or fallback assessment number
      const extractedNumber = parseInt(formData.title.replace(/\D/g, ''), 10);
      const computedAssessmentNumber = !isNaN(extractedNumber) && extractedNumber > 0
        ? extractedNumber
        : (timeline?.assessment_number || autoAssNumber || 1);

      // New CAMs can never be attached to a historical/inactive period. An
      // existing historical CAM can still be edited, and may be explicitly
      // reopened for remediation of failed learners.
      if (!timeline && !selectedPeriodIsActive) {
        throw new Error('New formative CAM windows can only be created for the current active Calendar semester.');
      }
      if (timeline && !selectedPeriodIsActive && formData.reopened_for_remediation && formData.assessment_status !== 'active') {
        throw new Error('A CAM reopened for remediation must have Active status.');
      }

      let deadlineIso = null;
      try {
        deadlineIso = new Date(formData.submission_deadline).toISOString();
      } catch (e) {
        deadlineIso = new Date(`${formData.end_date}T23:59:59`).toISOString();
      }

      const payload = {
        assessment_number: computedAssessmentNumber,
        assessment_type: 'formative',
        title: formData.title.trim(),
        total_marks: formData.total_marks || 30,
        start_date: formData.start_date,
        end_date: formData.end_date,
        submission_deadline: deadlineIso,
        assessment_status: formData.assessment_status || 'active',
        semester_id: formData.semester_id || null,
        calendar_semester_id: formData.calendar_semester_id || null,
        created_by: currentUserId,
        allow_trainer_override: formData.allow_trainer_override || false,
        original_total_marks: formData.original_total_marks || null,
        reopened_for_remediation: Boolean(formData.reopened_for_remediation),
      };

      if (timeline && !selectedPeriodIsActive && formData.reopened_for_remediation) {
        payload.assessment_status = 'active';
      }

      if (timeline?.isGrouped) {
        const idsToUpdate = timeline.timelineIds || [];
        if (idsToUpdate.length > 0) {
          const { error: updateErr } = await supabase
            .from('assessment_timelines')
            .update(payload)
            .in('id', idsToUpdate);
          if (updateErr) throw updateErr;
        } else if (timeline.unitIds) {
          const { error: updateErr } = await supabase
            .from('assessment_timelines')
            .update(payload)
            .in('unit_id', timeline.unitIds)
            .eq('title', timeline.originalTitle);
          if (updateErr) throw updateErr;
        }
      } else if (timeline?.id) {
        const { error: updateErr } = await supabase
          .from('assessment_timelines')
          .update({ ...payload, unit_id: formData.unit_id || timeline.unit_id })
          .eq('id', timeline.id);
        if (updateErr) throw updateErr;
      } else {
        // Create formative assessment timeline for all active department units
        const unitsToAssign = availableUnits.length > 0 ? availableUnits : [{ id: null }];
        const inserts = unitsToAssign.map(unit => ({
          ...payload,
          unit_id: unit.id,
          created_by: currentUserId
        }));

        let insertError = null;
        try {
          const { error: batchErr } = await supabase
            .from('assessment_timelines')
            .insert(inserts);
          if (batchErr) insertError = batchErr;
        } catch (bErr) {
          insertError = bErr;
        }

        // If batch insert had an issue, fallback to creating through timeline service
        if (insertError) {
          console.warn('Batch insert error, running through service createTimeline:', insertError);
          for (const unit of unitsToAssign) {
            await assessmentService.timeline.createTimeline({
              ...payload,
              unit_id: unit.id,
              created_by: currentUserId
            });
          }
        }
      }

      onSuccess?.();
      onClose();
    } catch (err) {
      console.error('Error saving timeline:', err);
      setError(err?.message || 'Failed to save assessment timeline');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-40 backdrop-blur-sm flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full overflow-hidden border border-slate-200 animate-in fade-in zoom-in-95 duration-150">
        
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100 bg-slate-50">
          <div>
            <h2 className="text-lg font-semibold text-slate-900">
              {timeline ? 'Edit Assessment Timeline' : 'New Assessment Window'}
            </h2>
            <p className="text-xs text-slate-500 mt-0.5">
              Assessment #{timeline?.assessment_number || autoAssNumber} (auto-generated)
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-slate-600 rounded-lg p-1 hover:bg-slate-200/60 transition-colors"
            disabled={loading}
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {error && (
          <div className="mx-6 mt-4 bg-red-50 border border-red-200 rounded-lg p-3 flex items-center space-x-2">
            <AlertCircle className="h-4 w-4 text-red-500 flex-shrink-0" />
            <span className="text-red-700 text-xs font-medium">{error}</span>
          </div>
        )}

        <form onSubmit={handleSubmit} className="p-6 space-y-4">

          {/* Institution Calendar period */}
          <div>
            <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wider mb-1.5">
              Calendar Semester <span className="text-red-500">*</span>
            </label>
            {(formData.calendar_semester_id || (!timeline && calendarSemesters.length > 0)) ? (
              <select
                name="calendar_semester_id"
                value={formData.calendar_semester_id}
                onChange={handleInputChange}
                className="w-full border border-slate-300 rounded-lg px-3.5 py-2 text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-blue-600/30 focus:border-blue-600 transition-colors bg-white disabled:bg-slate-100 disabled:text-slate-500"
                required
                disabled={loading || Boolean(timeline)}
              >
                <option value="">Select a Calendar semester</option>
                {calendarSemesters
                  .filter(period => isAcademicSemesterOpen({ ...period, calendar_status: period.status }) || String(period.id) === String(formData.calendar_semester_id))
                  .map(period => (
                    <option key={period.id} value={period.id}>
                      {calendarPeriodLabel(period) || 'Calendar period'}{isAcademicSemesterOpen({ ...period, calendar_status: period.status }) ? ' · Active' : ' · Historical'}
                    </option>
                  ))}
              </select>
            ) : (
              <select
                name="semester_id"
                value={formData.semester_id}
                onChange={handleInputChange}
                className="w-full border border-slate-300 rounded-lg px-3.5 py-2 text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-blue-600/30 focus:border-blue-600 transition-colors bg-white disabled:bg-slate-100 disabled:text-slate-500"
                required
                disabled={loading || Boolean(timeline)}
              >
                <option value="">Select an academic semester</option>
                {semesterPeriods
                  .filter(period => isAcademicSemesterOpen(period) || String(period.id) === String(formData.semester_id))
                  .map(period => (
                    <option key={period.id} value={period.id}>
                      {calendarPeriodLabel(findCalendarPeriod(period, calendarSemesters)) || (period.start_date ? `Academic period · ${period.start_date}` : 'Academic period')}{isAcademicSemesterOpen(period) ? ' · Active' : ' · Historical'}
                    </option>
                  ))}
              </select>
            )}
            {!timeline && <p className="mt-1 text-[11px] text-slate-500">New CAM windows are limited to the current active Calendar semester. The sequence starts at CAM 1 for each Calendar period.</p>}
            {timeline && !selectedPeriodIsActive && <p className="mt-1 text-[11px] text-amber-700">This is a historical semester. You may edit it or reopen it for failed-learner remediation.</p>}
          </div>
          
          {/* Assessment Title */}
          <div>
            <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wider mb-1.5">
              Assessment Title <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="title"
              value={formData.title}
              onChange={handleInputChange}
              placeholder="e.g. CAM 1"
              className="w-full border border-slate-300 rounded-lg px-3.5 py-2 text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-blue-600/30 focus:border-blue-600 transition-colors"
              required
              disabled={loading}
            />
          </div>

          {/* Marks & Status in 2 columns */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wider mb-1.5">
                Total Marks <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                name="total_marks"
                value={formData.total_marks}
                onChange={handleInputChange}
                min="1"
                max="30"
                step="1"
                className="w-full border border-slate-300 rounded-lg px-3.5 py-2 text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-blue-600/30 focus:border-blue-600 transition-colors"
                required
                disabled={loading}
              />
              <span className="text-[10px] text-slate-400">Capped at max 30 per CAM</span>
            </div>

            <div>
              <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wider mb-1.5">
                Status
              </label>
              <select
                name="assessment_status"
                value={formData.assessment_status}
                onChange={handleInputChange}
                className="w-full border border-slate-300 rounded-lg px-3.5 py-2 text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-blue-600/30 focus:border-blue-600 transition-colors bg-white"
                disabled={loading}
              >
                <option value="active">Active</option>
                <option value="scheduled">Scheduled</option>
                <option value="draft">Draft</option>
                <option value="completed">Completed</option>
              </select>
            </div>
          </div>

          {timeline && !selectedPeriodIsActive && (
            <label className="flex items-start gap-3 rounded-lg border border-violet-200 bg-violet-50/60 p-3 cursor-pointer">
              <input
                type="checkbox"
                name="reopened_for_remediation"
                checked={Boolean(formData.reopened_for_remediation)}
                onChange={handleInputChange}
                className="mt-0.5 h-4 w-4 rounded accent-violet-600"
                disabled={loading}
              />
              <span>
                <span className="block text-xs font-semibold text-violet-950">Reopen for failed-learner remediation</span>
                <span className="mt-0.5 block text-[11px] leading-snug text-violet-800">Allows marks to be corrected for learners who failed this CAM. Clear it to close the window again.</span>
              </span>
            </label>
          )}

          {/* Start and Closing Dates in 2 columns */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wider mb-1.5">
                Start Date <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                name="start_date"
                value={formData.start_date}
                onChange={handleInputChange}
                className="w-full border border-slate-300 rounded-lg px-3.5 py-2 text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-blue-600/30 focus:border-blue-600 transition-colors"
                required
                disabled={loading}
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wider mb-1.5">
                Closing Date <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                name="end_date"
                value={formData.end_date}
                onChange={handleInputChange}
                className="w-full border border-slate-300 rounded-lg px-3.5 py-2 text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-blue-600/30 focus:border-blue-600 transition-colors"
                required
                disabled={loading}
              />
            </div>
          </div>

          {/* Submission Date (Critical for POE Upload) */}
          <div>
            <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wider mb-1.5 flex items-center justify-between">
              <span>Submission Date (POE Upload Deadline) <span className="text-red-500">*</span></span>
              <span className="text-[11px] text-blue-600 font-medium">Used for POE upload</span>
            </label>
            <input
              type="datetime-local"
              name="submission_deadline"
              value={formData.submission_deadline}
              onChange={handleInputChange}
              className="w-full border border-slate-300 rounded-lg px-3.5 py-2 text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-blue-600/30 focus:border-blue-600 transition-colors"
              required
              disabled={loading}
            />
          </div>

          {/* Scope Indicator for creation */}
          {!timeline && (
            <div className="pt-2 text-xs text-slate-500 flex items-center gap-1.5">
              <span className="w-1.5 h-1.5 rounded-full bg-blue-500"></span>
              <span>Automatically applies to all active department units ({availableUnits.length} units).</span>
            </div>
          )}

          {/* Actions */}
          <div className="flex justify-end items-center gap-3 pt-4 border-t border-slate-100">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-slate-600 hover:text-slate-800 rounded-lg hover:bg-slate-100 transition-colors"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="flex items-center space-x-2 px-5 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 shadow-sm transition-colors disabled:opacity-50"
              disabled={loading}
            >
              {loading && <div className="animate-spin rounded-full h-3.5 w-3.5 border-b-2 border-white"></div>}
              <Save className="h-4 w-4" />
              <span>{timeline ? 'Save Changes' : 'Create Window'}</span>
            </button>
          </div>

        </form>
      </div>
    </div>
  );
};

export default AssessmentTimelineModal;
